"use client";
import { useEffect, useState } from "react";
import { Cherry, Moon, Sun } from "lucide-react";

import { Button } from "@/components/ui/button";

export function ThemeToggle() {
  const [mounted, setMounted] = useState(false);
  const [isLight, setIsLight] = useState(true);

  useEffect(() => {
    const storedTheme =
      localStorage.getItem("theme");

    const prefersDark =
      window.matchMedia(
        "(prefers-color-scheme: dark)"
      ).matches;

    const shouldBeDark =
      storedTheme === "dark" ||
      (!storedTheme && prefersDark);

    setIsLight(!shouldBeDark);

    document.documentElement.classList.toggle(
      "dark",
      shouldBeDark
    );

    setMounted(true);
  }, []);

  function toggleTheme() {
    const nextIsLight = !isLight;

    setIsLight(nextIsLight);

    document.documentElement.classList.toggle(
      "dark",
      !nextIsLight
    );

    localStorage.setItem(
      "theme",
      nextIsLight ? "light" : "dark"
    );
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggleTheme}
      aria-label={`Switch to ${isLight ? "dark" : "light"} mode`}
      className="
        group relative h-10 w-10
        overflow-visible rounded-full
        bg-transparent p-0
        hover:bg-transparent
      "
    >
      {/* Outer light ring */}
      <span
        className={`
          absolute inset-[4px] rounded-full border
          transition-all duration-300
          ${
            isLight
              ? "scale-100 border-[#9D2235]/50"
              : "scale-90 border-muted-foreground/30"
          }
        `}
      />

      {/* Top ray */}
      <span
        className={`
          absolute left-1/2 top-0 w-px -translate-x-1/2
          bg-[#9D2235]
          transition-all duration-300
          ${
            isLight
              ? "h-[5px] opacity-70 group-hover:-translate-y-[1px]"
              : "h-0 opacity-0"
          }
        `}
      />

      {/* Bottom ray */}
      <span
        className={`
          absolute bottom-0 left-1/2 w-px -translate-x-1/2
          bg-[#9D2235]
          transition-all duration-300
          ${
            isLight
              ? "h-[5px] opacity-70 group-hover:translate-y-[1px]"
              : "h-0 opacity-0"
          }
        `}
      />

      {/* Left ray */}
      <span
        className={`
          absolute left-0 top-1/2 h-px -translate-y-1/2
          bg-[#9D2235]
          transition-all duration-300
          ${
            isLight
              ? "w-[5px] opacity-70 group-hover:-translate-x-[1px]"
              : "w-0 opacity-0"
          }
        `}
      />

      {/* Right ray */}
      <span
        className={`
          absolute right-0 top-1/2 h-px -translate-y-1/2
          bg-[#9D2235]
          transition-all duration-300
          ${
            isLight
              ? "w-[5px] opacity-70 group-hover:translate-x-[1px]"
              : "w-0 opacity-0"
          }
        `}
      />

      {/* Cherry */}
      <span
        className={`
          relative z-10 flex h-7 w-7
          items-center justify-center rounded-full
          transition-all duration-300
          ${
            isLight
              ? "bg-[#9D2235] text-white"
              : "bg-[#9D2235]/15 text-[#9D2235]"
          }
        `}
      >
        <Cherry className="h-[15px] w-[15px]" strokeWidth={2.3} />
      </span>
    </Button>
  );
}